import {
  Autocomplete,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import GKModal from "components/GKModal";
import GKSearchSelect from "components/GKSearchSelect";
import GKSelect from "components/GKSelect";
import GKTextArea from "components/GKTextarea";
import GKTextField from "components/GKTextField";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { RiDeleteBinLine } from "react-icons/ri";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { CORPORATE_ACTION_TYPE } from "utils/constants";
import { QueryKeys } from "utils/queryKey";

function AddCorporateActionModal({ isModalOpen, setIsModalOpen, refetch }) {
  const { id } = useParams();

  const { data, isFetching } = useQuery({
    queryKey: [QueryKeys.CORPORATE_ACTION_COMPANY_LIST],
    queryFn: () => axiosInstance.get("/fetch_all_short_active/"),
  });

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      act_type: "",
      act_date: "",
      per_share: "",
      for_every: "",
      new_share_number: "",
      bonus_amount: "",
      existing_asset_quantity: "",
      new_asset_quantity: "",
      parent_instrument: "",
      cost_acquisition_ratio: "",
      re_gcode: "",
      premium: "",
      purpose: "",
      closing_date: "",
      demerged_companies: [
        {
          instrument: "",
          new_asset_quantity: "",
          cost_acquisition_ratio: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "demerged_companies",
  });

  const onSubmit = async (values) => {
    try {
      await axiosInstance.post(`/admin-instrument-ca/${id}/create`, values);
      refetch();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const act_type = watch("act_type");

  return (
    <GKModal
      open={isModalOpen}
      setOpen={setIsModalOpen}
      modalTitle={"Add New Corporate Action"}
      footer={
        <Grid container justifyContent={"flex-end"} gap={1}>
          <Button
            variant="outlined"
            onClick={() => setIsModalOpen(false)}
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
          >
            Close
          </Button>
          <Button
            type="submit"
            form="corporate-action-form"
            variant="outlined"
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
          >
            Submit
          </Button>
        </Grid>
      }
    >
      <form id="corporate-action-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="act_type"
                control={control}
                render={({ field }) => (
                  <GKSelect {...field} displayEmpty inputLabel="Action Type">
                    <MenuItem style={{ display: "none" }} value="">
                      Select Code Type
                    </MenuItem>
                    {CORPORATE_ACTION_TYPE?.map((dataItem, i) => (
                      <MenuItem key={i} value={dataItem.value}>
                        {dataItem.name}
                      </MenuItem>
                    ))}
                  </GKSelect>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="act_date"
                control={control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    type="date"
                    inputLabel="Action Date"
                  />
                )}
              />
            </FormControl>
          </Grid>

          {act_type === "DIV" && (
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <Controller
                  name="per_share"
                  control={control}
                  render={({ field }) => (
                    <GKTextField
                      {...field}
                      type="number"
                      inputLabel="Per share"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          )}
          {(act_type === "SPL" || act_type === "RIGHTS") && (
            <>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="re_gcode"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="For Every"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="new_share_number"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="New Share Number"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </>
          )}
          {act_type === "BNS" && (
            <>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="for_every"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="For Every"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="bonus_amount"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="Bonus Amount"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </>
          )}
          {act_type === "MRG" && (
            <>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="existing_asset_quantity"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        inputLabel="Existing Asset Quantity"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    type="number"
                    name="new_asset_quantity"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="New Asset Quantity"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="parentInstrument"
                    control={control}
                    render={({ field }) => (
                      <GKSearchSelect
                        {...field}
                        options={
                          data?.data.map((item) => ({
                            name: item.companyName,
                            value: item.gcode,
                            isin: item.isin,
                          })) || []
                        }
                        getOptionLabel={(option) => option.name}
                        onChange={(event, val) => field.onChange(val.value)}
                        renderInput={(params) => (
                          <GKTextField
                            {...params}
                            inputLabel="Parent Instrument"
                            placeholder="Select Company Name"
                          />
                        )}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </>
          )}
          {act_type === "DMG" && (
            <>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="existing_asset_quantity"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="Existing Asset Quantity"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="new_asset_quantity"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="New Asset Quantity"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="cost_acquisition_ratio"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="Cost Acquisition Ratio"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" fontWeight="bold">
                  Demerged Companies
                </Typography>
              </Grid>
              {fields.map((field, index) => (
                <>
                  <Grid item xs={6}>
                    <FormControl variant="standard" fullWidth>
                      <Controller
                        name={`${index}`}
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            disabled={isFetching}
                            options={
                              data?.data.map((item) => ({
                                name: item.companyName,
                                value: item.gcode,
                                isin: item.isin,
                              })) || []
                            }
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                              <GKTextField
                                {...params}
                                inputLabel="Child Instrument"
                                placeholder="Select Company Name"
                              />
                            )}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl variant="standard" fullWidth>
                      <Controller
                        name={`demerged_companies.${index}.new_asset_quantity`}
                        control={control}
                        render={({ field }) => (
                          <GKTextField
                            {...field}
                            type="number"
                            inputLabel="New Asset Quantity Child"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl variant="standard" fullWidth>
                      <Controller
                        name={`demerged_companies.${index}.cost_acquisition_ratio`}
                        control={control}
                        render={({ field }) => (
                          <GKTextField
                            {...field}
                            type="number"
                            inputLabel="Cost Acquisition Ratio Child"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} container justifyContent="flex-end">
                    <IconButton onClick={() => remove(index)}>
                      <RiDeleteBinLine />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </>
              ))}
              <Grid item xs={12}>
                <Button
                  sx={{
                    fontSize: "0.7rem",
                    fontFamily: '"Arial", sans-serif',
                    textTransform: "none",
                  }}
                  onClick={() =>
                    append({
                      instrument: "",
                      new_asset_quantity: "",
                      cost_acquisition_ratio: "",
                    })
                  }
                  variant="outlined"
                >
                  Add Stock
                </Button>
              </Grid>
            </>
          )}
          {(act_type === "RIGHTS" || act_type === "REC") && (
            <>
              <Grid item xs={6}>
                <GKSearchSelect
                  inputLabel={
                    act_type === "RIGHTS"
                      ? "Right Issue Instrument"
                      : "Partly Paid Instrument"
                  }
                  disabled={isFetching}
                  options={
                    data?.data.map((item) => ({
                      name: item.companyName,
                      value: item.gcode,
                      isin: item.isin,
                    })) || []
                  }
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <GKTextField
                      {...params}
                      variant="outlined"
                      placeholder="Select Company Name"
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl variant="standard" fullWidth>
                  <Controller
                    name="premium"
                    control={control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        inputLabel="Premium"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </>
          )}
          {act_type === "REC" && (
            <Grid item xs={12}>
              <FormControl variant="standard" fullWidth>
                <Controller
                  name="closing_date"
                  control={control}
                  render={({ field }) => (
                    <GKTextField
                      {...field}
                      type="date"
                      inputLabel="Closing Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <Controller
              name="purpose"
              control={control}
              render={({ field }) => (
                <GKTextArea
                  {...field}
                  minRows={5}
                  inputLabel="Purpose"
                  style={{
                    width: "97%",
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </GKModal>
  );
}

export default AddCorporateActionModal;
